import React, { useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { SALUTATIONS, TITLES, GENDERS, MONTHS_DE } from './constants';
import { FieldLabel, CustomFields, usePlzCities, CitySuggestions, PhoneField } from './shared';
import { cityKeptOrFilled } from '@/lib/plzOrt';
import { formatCourseOfferLine } from './courseOfferLabel';
import { payerCopiedLine, studentFromPayer } from './utils';

export default function StepStudent({ form, setField, setStudent, setCustom, F, title, customFields, invalid }) {
  const s = form.student;
  const ec = (k) => (invalid(k) ? 'border-red-500 focus-visible:ring-red-500 bg-red-50/50' : '');
  const plzCities = usePlzCities(s.address_same ? '' : s.postal_code);
  useEffect(() => {
    const next = cityKeptOrFilled(s.city, plzCities);
    if (next !== (s.city ?? '')) setField('student', 'city', next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plzCities, s.city]);
  const markSameAsPayer = (on) => {
    if (on) setStudent((prev) => studentFromPayer(prev, form.payer));
    else setStudent((prev) => ({ ...prev, same_as_payer: false }));
  };

  return (
    <div className="space-y-5" data-testid="public-registration-step-student">
      <h2 className="text-lg md:text-lg font-semibold text-slate-900">{title}</h2>

      <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-600" data-testid="public-registration-student-subject-recap">
        <span>
          {form.course_offer ? (
            <>Gewähltes Kursangebot: <strong className="text-slate-900">{formatCourseOfferLine(form.course_offer)}</strong></>
          ) : (
            <>
              Gewähltes Fach: <strong className="text-slate-900">{s.instrument}</strong>
              {form.teaching_type ? ` · ${form.teaching_type}` : ''}
            </>
          )}
        </span>
      </div>

      <div className="space-y-2">
        <label className="flex items-start gap-3 cursor-pointer" data-testid="public-registration-student-same-payer-row">
          <Checkbox
            checked={!!s.same_as_payer}
            onCheckedChange={(v) => markSameAsPayer(!!v)}
            data-testid="public-registration-student-same-payer"
          />
          <span>
            <span className="text-slate-800">Teilnehmer ist gleichzeitig Zahlungspflichtiger</span>
            <span className="block text-sm text-slate-500">Name, Anschrift und Kontaktdaten werden übernommen.</span>
          </span>
        </label>
        {s.same_as_payer && (
          <p className="text-sm text-slate-600 pl-7" data-testid="public-registration-student-payer-recap">
            {payerCopiedLine(form.payer)}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {F('student.salutation').visible && (
          <div>
            <FieldLabel spec={F('student.salutation')} />
            <Select value={s.salutation} onValueChange={(v) => setField('student', 'salutation', v)}>
              <SelectTrigger data-testid="public-registration-student-salutation"><SelectValue placeholder="Bitte wählen…" /></SelectTrigger>
              <SelectContent>
                {SALUTATIONS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {F('student.title').visible && (
          <div>
            <FieldLabel spec={F('student.title')} />
            <Select value={s.title} onValueChange={(v) => setField('student', 'title', v)}>
              <SelectTrigger data-testid="public-registration-student-title"><SelectValue placeholder="Bitte wählen…" /></SelectTrigger>
              <SelectContent>
                {TITLES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <FieldLabel spec={F('student.first_name')} error={invalid('student.first_name')} />
          <Input placeholder="Max" className={ec('student.first_name')} value={s.first_name} onChange={(e) => setField('student', 'first_name', e.target.value)} data-testid="public-registration-student-firstname" />
        </div>
        <div>
          <FieldLabel spec={F('student.last_name')} error={invalid('student.last_name')} />
          <Input placeholder="Mustermann" className={ec('student.last_name')} value={s.last_name} onChange={(e) => setField('student', 'last_name', e.target.value)} data-testid="public-registration-student-lastname" />
        </div>
      </div>

      {!s.same_as_payer && (
        <label className="flex items-center gap-3 cursor-pointer" data-testid="public-registration-student-addresssame-row">
          <Switch
            checked={s.address_same}
            onCheckedChange={(v) => setField('student', 'address_same', !!v)}
            data-testid="public-registration-student-addresssame-toggle"
          />
          <span className="text-slate-800">Adresse von zahlungspflichtiger Person übernehmen</span>
        </label>
      )}

      {!s.address_same && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <Label className={invalid('student.street') ? 'text-red-600' : ''}>Straße *</Label>
              <Input placeholder="Musterstr." className={ec('student.street')} value={s.street} onChange={(e) => setField('student', 'street', e.target.value)} data-testid="public-registration-student-street" />
            </div>
            <div>
              <Label className={invalid('student.house_number') ? 'text-red-600' : ''}>Hausnr. *</Label>
              <Input placeholder="221" inputMode="numeric" className={ec('student.house_number')} value={s.house_number} onChange={(e) => setField('student', 'house_number', e.target.value.replace(/\D/g, ''))} data-testid="public-registration-student-housenumber" />
            </div>
            <div>
              <Label>Zusatz</Label>
              <Input placeholder="b" value={s.address_extra} onChange={(e) => setField('student', 'address_extra', e.target.value)} data-testid="public-registration-student-addressextra" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className={invalid('student.postal_code') ? 'text-red-600' : ''}>Postleitzahl *</Label>
              <Input placeholder="PLZ" inputMode="numeric" maxLength={5} className={ec('student.postal_code')} value={s.postal_code} onChange={(e) => setField('student', 'postal_code', e.target.value.replace(/\D/g, '').slice(0, 5))} data-testid="public-registration-student-postal" />
            </div>
            <div className="md:col-span-2">
              <Label className={invalid('student.city') ? 'text-red-600' : ''}>Ort *</Label>
              <Input placeholder="Wohnort" className={ec('student.city')} value={s.city} onChange={(e) => setField('student', 'city', e.target.value)} data-testid="public-registration-student-city" />
              <CitySuggestions cities={plzCities} current={s.city} onPick={(c) => setField('student', 'city', c)} testPrefix="public-registration-student" />
            </div>
            <div>
              <Label>Ortsteil</Label>
              <Input value={s.district} onChange={(e) => setField('student', 'district', e.target.value)} data-testid="public-registration-student-district" />
            </div>
          </div>
        </>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {F('student.gender').visible && (
          <div>
            <FieldLabel spec={F('student.gender')} error={invalid('student.gender')} />
            <Select value={s.gender} onValueChange={(v) => setField('student', 'gender', v)}>
              <SelectTrigger className={ec('student.gender')} data-testid="public-registration-student-gender"><SelectValue placeholder="Bitte wählen…" /></SelectTrigger>
              <SelectContent>
                {GENDERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {F('student.birthdate').visible && (
          <>
            <div>
              <FieldLabel spec={{ ...F('student.birthdate'), label: 'Geburtstag' }} error={invalid('student.birthdate')} />
              <Select value={s.birth_day} onValueChange={(v) => setField('student', 'birth_day', v)}>
                <SelectTrigger className={ec('student.birthdate')} data-testid="public-registration-student-birthday"><SelectValue placeholder="--" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {Array.from({ length: 31 }, (_, i) => String(i + 1)).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <FieldLabel spec={{ ...F('student.birthdate'), label: 'Monat' }} error={invalid('student.birthdate')} />
              <Select value={s.birth_month} onValueChange={(v) => setField('student', 'birth_month', v)}>
                <SelectTrigger className={ec('student.birthdate')} data-testid="public-registration-student-birthmonth"><SelectValue placeholder="--" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {MONTHS_DE.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <FieldLabel spec={{ ...F('student.birthdate'), label: 'Jahr' }} error={invalid('student.birthdate')} />
              <Select value={s.birth_year} onValueChange={(v) => setField('student', 'birth_year', v)}>
                <SelectTrigger className={ec('student.birthdate')} data-testid="public-registration-student-birthyear"><SelectValue placeholder="--" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {Array.from({ length: 100 }, (_, i) => String(new Date().getFullYear() - i)).map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {F('student.email').visible && (
          <div>
            <FieldLabel spec={F('student.email')} error={invalid('student.email')} />
            <Input type="email" placeholder="max.mustermann@beispiel.de" className={ec('student.email')} value={s.email} onChange={(e) => setField('student', 'email', e.target.value)} data-testid="public-registration-student-email" />
          </div>
        )}
        {F('student.phone').visible && (
          <div>
            <FieldLabel spec={F('student.phone')} error={invalid('student.phone')} />
            <PhoneField value={s.phone} onChange={(v) => setField('student', 'phone', v)} error={invalid('student.phone')} testId="public-registration-student-phone" />
          </div>
        )}
      </div>

      {F('student.prior_knowledge').visible && (
        <div>
          <FieldLabel spec={F('student.prior_knowledge')} />
          <Textarea
            rows={3}
            value={s.prior_knowledge}
            onChange={(e) => setField('student', 'prior_knowledge', e.target.value)}
            placeholder="Notenlesen, Harmonielehre, Instrumentenkenntnisse..."
            data-testid="public-registration-student-priorknowledge"
          />
        </div>
      )}

      {F('student.photo_consent').visible && (
        <div className="space-y-2">
          <p className="text-slate-800">
            Ich bin damit einverstanden, dass im Rahmen der Öffentlichkeitsarbeit der Musikschule Fotos und Daten des
            Teilnehmers verwendet und Gruppenfotos sowie Einzelfotos auf der Website der Musikschule bzw. dritter
            Anbieter, in Sozialen Medien oder in Printmedien veröffentlicht werden.
          </p>
          <label className="flex items-center gap-3 cursor-pointer" data-testid="public-registration-student-photoconsent-row">
            <Checkbox
              checked={s.photo_consent}
              onCheckedChange={(v) => setField('student', 'photo_consent', !!v)}
              data-testid="public-registration-student-photoconsent"
            />
            <span className="text-slate-800">Hiermit erteile ich eine Foto-Erlaubnis</span>
          </label>
        </div>
      )}

      <CustomFields customFields={customFields} form={form} setCustom={setCustom} invalid={invalid} testPrefix="public-registration-student" />
    </div>
  );
}
